import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Upload, Camera, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const INDUSTRIES = ['Automotive', 'Medical', 'Real Estate', 'Tech', 'Finance', 'Legal', 'Insurance', 'Construction', 'Dental', 'Retail', 'Marketing/Media', 'Manufacturing', 'Food & Hospitality', 'Agriculture', 'Energy', 'Logistics', 'Education', 'Other'];
const PLATFORMS = ['LinkedIn', 'X', 'Instagram', 'YouTube', 'Newsletter', 'Podcast', 'TikTok', 'Facebook'];

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [profile, setProfile] = useState({
    headline: user?.headline || '',
    bio: user?.bio || '',
    industries: user?.industries || [],
    platforms: user?.platforms || ['LinkedIn'],
    linkedin_url: user?.linkedin_url || '',
    linkedin_connections: user?.linkedin_connections || 0,
    follower_counts: user?.follower_counts || {},
    years_in_industry: user?.years_in_industry || 0,
    current_company: user?.current_company || '',
    headshot_url: user?.headshot_url || '',
    audience_quality: user?.audience_quality || '',
    tools_used: user?.tools_used || '',
    x_handle: user?.x_handle || '',
    newsletter_url: user?.newsletter_url || '',
    facebook_url: user?.facebook_url || '',
    instagram_handle: user?.instagram_handle || '',
    tiktok_handle: user?.tiktok_handle || '',
    podcast_url: user?.podcast_url || '',
  });

  const toggleIndustry = (ind) => {
    setProfile(p => ({ ...p, industries: p.industries.includes(ind) ? p.industries.filter(i => i !== ind) : [...p.industries, ind] }));
  };

  const togglePlatform = (plat) => {
    setProfile(p => ({ ...p, platforms: p.platforms.includes(plat) ? p.platforms.filter(i => i !== plat) : [...p.platforms, plat] }));
  };

  const handleHeadshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File must be under 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      if (data.url) setProfile(p => ({ ...p, headshot_url: data.url }));
    } catch (e) { alert('Upload failed. Please try again.'); }
    finally { setUploading(false); }
  };

  const handleGenerateBio = async () => {
    try {
      const { data } = await axios.post(`${API}/profile/generate-bio`, {}, { headers: getAuthHeaders() });
      if (data.bio) setProfile(p => ({ ...p, bio: data.bio }));
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, profile, { headers: getAuthHeaders() });
      await refreshUser();
      if (step < 3) setStep(step + 1);
      else navigate('/dashboard');
    } catch { alert('Failed to save profile'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center px-4 py-12" data-testid="onboarding-page">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="Freddy Media" className="h-10 w-10 mx-auto mb-4" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Complete Your <span className="text-[#FF4343]">Profile</span></h1>
          <p className="text-[#9CA3AF] text-sm">Step {step} of 3</p>
          <div className="flex gap-2 justify-center mt-4">
            {[1,2,3].map(s => <div key={s} className={`h-1 w-16 ${s <= step ? 'bg-[#FF4343]' : 'bg-white/10'} transition-colors`} />)}
          </div>
        </div>

        <div className="glass p-4 sm:p-8">
          {step === 1 && (
            <div className="space-y-6" data-testid="onboarding-step-1">
              {/* Headshot Upload */}
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-3 block">Profile Photo</label>
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20 bg-[#1F2937] border border-white/10 overflow-hidden flex-shrink-0">
                    {profile.headshot_url ? (
                      <img src={profile.headshot_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-[#6B7280] m-auto mt-6" />
                    )}
                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
                      <Camera className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  <div>
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-headshot-btn"
                      className="text-xs px-4 py-2 border border-[#FF4343]/50 text-[#FF4343] hover:bg-[#FF4343]/10 transition-all disabled:opacity-50">
                      {uploading ? 'Uploading...' : profile.headshot_url ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    <p className="text-[10px] text-[#6B7280] mt-1">JPG, PNG under 5MB</p>
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleHeadshotUpload} className="hidden" />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-3 block">Industries You Operate In</label>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES.map(ind => (
                    <button key={ind} onClick={() => toggleIndustry(ind)} data-testid={`industry-${ind}`}
                      className={`text-xs px-3 py-1.5 border transition-colors ${
                        profile.industries.includes(ind) ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF] hover:text-white'
                      }`}>{ind}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Current Title & Role</label>
                <input value={profile.headline} onChange={e => setProfile({...profile, headline: e.target.value})} placeholder="e.g., Dealer Principal | Johnson Auto Group" data-testid="onboarding-headline"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Company</label>
                  <input value={profile.current_company} onChange={e => setProfile({...profile, current_company: e.target.value})} placeholder="Your company" data-testid="onboarding-company"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Years in Industry</label>
                  <input type="number" value={profile.years_in_industry} onChange={e => setProfile({...profile, years_in_industry: parseInt(e.target.value) || 0})} data-testid="onboarding-years"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="onboarding-step-2">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-3 block">Platforms You're Active On</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {PLATFORMS.map(p => (
                    <button key={p} onClick={() => togglePlatform(p)} data-testid={`platform-${p}`}
                      className={`text-xs px-3 py-1.5 border transition-colors ${
                        profile.platforms.includes(p) ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF] hover:text-white'
                      }`}>{p}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">LinkedIn Profile URL *</label>
                <input value={profile.linkedin_url} onChange={e => setProfile({...profile, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/yourname" data-testid="onboarding-linkedin-url"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">X (Twitter) Handle</label>
                  <input value={profile.x_handle} onChange={e => setProfile({...profile, x_handle: e.target.value})} placeholder="@yourhandle" data-testid="onboarding-x-handle"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">LinkedIn Connections</label>
                  <input type="number" value={profile.linkedin_connections} onChange={e => setProfile({...profile, linkedin_connections: parseInt(e.target.value) || 0})} data-testid="onboarding-connections"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Newsletter URL</label>
                  <input value={profile.newsletter_url} onChange={e => setProfile({...profile, newsletter_url: e.target.value})} placeholder="https://yournewsletter.com" data-testid="onboarding-newsletter"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Podcast URL</label>
                  <input value={profile.podcast_url} onChange={e => setProfile({...profile, podcast_url: e.target.value})} placeholder="https://podcast.com/show" data-testid="onboarding-podcast"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Instagram</label>
                  <input value={profile.instagram_handle} onChange={e => setProfile({...profile, instagram_handle: e.target.value})} placeholder="@handle" data-testid="onboarding-instagram"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">TikTok</label>
                  <input value={profile.tiktok_handle} onChange={e => setProfile({...profile, tiktok_handle: e.target.value})} placeholder="@handle" data-testid="onboarding-tiktok"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Facebook</label>
                  <input value={profile.facebook_url} onChange={e => setProfile({...profile, facebook_url: e.target.value})} placeholder="URL or handle" data-testid="onboarding-facebook"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Tools You Use</label>
                <input value={profile.tools_used} onChange={e => setProfile({...profile, tools_used: e.target.value})} placeholder="e.g., VinSolutions, HubSpot, Salesforce" data-testid="onboarding-tools"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="onboarding-step-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Short Bio (200 chars max)</label>
                <textarea value={profile.bio} onChange={e => setProfile({...profile, bio: e.target.value.slice(0, 200)})} rows={3} placeholder="How would you describe yourself to a brand?" data-testid="onboarding-bio"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] resize-none" />
                <div className="flex justify-between mt-1">
                  <button onClick={handleGenerateBio} className="text-xs text-[#FF4343] hover:underline" data-testid="generate-bio-btn">AI Generate Bio</button>
                  <span className="text-xs text-[#6B7280]">{profile.bio.length}/200</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Audience Quality</label>
                <select value={profile.audience_quality} onChange={e => setProfile({...profile, audience_quality: e.target.value})} data-testid="onboarding-audience"
                  className="w-full px-4 py-3 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                  <option value="">Select...</option>
                  {['Peers in my industry', 'Business owners', 'Mixed', 'Other'].map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} data-testid="onboarding-back"
                className="flex items-center gap-2 border border-white/20 text-white hover:bg-white/5 px-6 py-2 text-sm font-medium transition-all">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            ) : <div />}
            <button onClick={handleSave} disabled={saving} data-testid="onboarding-next"
              className="flex items-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-6 py-2 text-sm font-semibold transition-all duration-300 disabled:opacity-50">
              {saving ? 'Saving...' : step < 3 ? 'Continue' : 'Complete Profile'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
